import Toast from "components/cms/Toast";
import SectionCard from "components/cms/SectionCard";
import {
  AreaInput,
  CardRow,
  FieldRow,
  GroupBox,
  TextInput,
} from "components/cms/fields";
import useCmsPage from "hooks/useCmsPage";
import {
  addContactPage,
  getContactPage,
  prepareContactPage,
  updateContactPage,
} from "api/contactpage";
import { emptyContact } from "utils/defaults";

const STEPS = ["nextStep1", "nextStep2", "nextStep3"];
const OFFICES = ["office1", "office2"];

const NEXT_KEYS = [
  "nextTag",
  "nextTitleLead",
  "nextTitleAccent",
  "nextTitleTail",
  "nextBody",
  "nextStep1",
  "nextStep2",
  "nextStep3",
  "nextHide",
];
const FORM_KEYS = [
  "formTitleLead",
  "formTitleLead2",
  "formTitleAccent",
  "formConsentPre",
  "formConsentLink",
  "formConsentHref",
  "formHide",
];
const OFFICES_KEYS = [
  "officesTag",
  "officesTitleLead",
  "officesTitleAccent",
  "officesBody",
  "office1",
  "office2",
  "officesHide",
];

export default function ContactPage() {
  const cms = useCmsPage({
    load: getContactPage,
    create: addContactPage,
    update: updateContactPage,
    empty: emptyContact,
    mapResponse: (res) => res.data?.data,
    prepare: prepareContactPage,
  });
  const {
    form,
    setField,
    frMap,
    exists,
    loading,
    toast,
    setToast,
    save,
    saveSection,
    savingSection,
    isSectionDirty,
  } = cms;

  const saveContact = (sectionId, keys) => {
    if (!exists) {
      save();
      return;
    }
    saveSection(sectionId, keys);
  };

  if (loading) {
    return <p className="mt-5 text-gray-600">Loading Contact…</p>;
  }

  return (
    <div className="mt-3">
      <Toast toast={toast} onClose={() => setToast(null)} />

      {!exists ? (
        <p className="mb-3 text-sm text-gray-500">
          Contact is not saved yet. Fill the sections and click Save — the first
          save creates the page.
        </p>
      ) : null}

      <SectionCard
        title="What happens next"
        hide={form.nextHide}
        hideId="contact-nextHide"
        onHideChange={(value) => setField("nextHide", value)}
        onSave={() => saveContact("next", NEXT_KEYS)}
        saving={savingSection === "next" || savingSection === "all"}
        canSave={!exists || isSectionDirty(NEXT_KEYS)}
      >
        <FieldRow>
          <TextInput
            label="Tag"
            path="nextTag"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <FieldRow>
          <TextInput
            label="Title lead"
            path="nextTitleLead"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="Title accent"
            path="nextTitleAccent"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <AreaInput
          label="Title tail"
          path="nextTitleTail"
          form={form}
          setField={setField}
          frMap={frMap}
          rows={2}
        />
        <AreaInput
          label="Body"
          path="nextBody"
          form={form}
          setField={setField}
          frMap={frMap}
        />
        <CardRow cols="md:grid-cols-3">
          {STEPS.map((key, index) => (
            <GroupBox key={key} title={`Step ${index + 1}`}>
              <TextInput
                label="Number"
                path={`${key}.n`}
                form={form}
                setField={setField}
              />
              <TextInput
                label="Title"
                path={`${key}.title`}
                form={form}
                setField={setField}
                frMap={frMap}
              />
              <AreaInput
                label="Description"
                path={`${key}.description`}
                form={form}
                setField={setField}
                frMap={frMap}
              />
            </GroupBox>
          ))}
        </CardRow>
      </SectionCard>

      <SectionCard
        title="Enquiry form"
        description="Title and privacy checkbox copy. Field labels stay fixed on the website."
        hide={form.formHide}
        hideId="contact-formHide"
        onHideChange={(value) => setField("formHide", value)}
        onSave={() => saveContact("form", FORM_KEYS)}
        saving={savingSection === "form" || savingSection === "all"}
        canSave={!exists || isSectionDirty(FORM_KEYS)}
      >
        <FieldRow>
          <TextInput
            label="Title lead"
            path="formTitleLead"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="Title lead 2"
            path="formTitleLead2"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <FieldRow>
          <TextInput
            label="Title accent"
            path="formTitleAccent"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <AreaInput
          label="Checkbox text"
          path="formConsentPre"
          form={form}
          setField={setField}
          frMap={frMap}
        />
        <FieldRow>
          <TextInput
            label="Privacy policy link text"
            path="formConsentLink"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="Privacy policy link"
            path="formConsentHref"
            form={form}
            setField={setField}
            placeholder="/legal/privacy"
          />
        </FieldRow>
      </SectionCard>

      <SectionCard
        title="Offices"
        hide={form.officesHide}
        hideId="contact-officesHide"
        onHideChange={(value) => setField("officesHide", value)}
        onSave={() => saveContact("offices", OFFICES_KEYS)}
        saving={savingSection === "offices" || savingSection === "all"}
        canSave={!exists || isSectionDirty(OFFICES_KEYS)}
      >
        <FieldRow>
          <TextInput
            label="Tag"
            path="officesTag"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <FieldRow>
          <TextInput
            label="Title lead"
            path="officesTitleLead"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="Title accent"
            path="officesTitleAccent"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <AreaInput
          label="Body"
          path="officesBody"
          form={form}
          setField={setField}
          frMap={frMap}
        />
        <CardRow>
          {OFFICES.map((key, index) => (
            <GroupBox key={key} title={`Office ${index + 1}`}>
              <FieldRow>
                <TextInput
                  label="Country"
                  path={`${key}.country`}
                  form={form}
                  setField={setField}
                  frMap={frMap}
                />
                <TextInput
                  label="City"
                  path={`${key}.city`}
                  form={form}
                  setField={setField}
                  frMap={frMap}
                />
              </FieldRow>
              <FieldRow>
                <TextInput
                  label="Badge"
                  path={`${key}.badge`}
                  form={form}
                  setField={setField}
                  frMap={frMap}
                />
                <TextInput
                  label="Hours"
                  path={`${key}.hours`}
                  form={form}
                  setField={setField}
                  frMap={frMap}
                />
              </FieldRow>
              <TextInput
                label="Address"
                path={`${key}.address`}
                form={form}
                setField={setField}
              />
              <FieldRow>
                <TextInput
                  label="Phone"
                  path={`${key}.phone`}
                  form={form}
                  setField={setField}
                />
                <TextInput
                  label="Email"
                  path={`${key}.email`}
                  form={form}
                  setField={setField}
                />
              </FieldRow>
              <FieldRow>
                <TextInput
                  label="Latitude"
                  path={`${key}.lat`}
                  form={form}
                  setField={setField}
                />
                <TextInput
                  label="Longitude"
                  path={`${key}.lon`}
                  form={form}
                  setField={setField}
                />
              </FieldRow>
            </GroupBox>
          ))}
        </CardRow>
      </SectionCard>
    </div>
  );
}
